import { type RefObject, useMemo, useState } from "react";
import {
    type Announcements,
    closestCenter,
    DndContext,
    type UniqueIdentifier,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { RowData } from "@tanstack/react-table";
import type { ColumnPin } from "@common/types/views/views";
import { ColumnDragOverlay } from "@design-system/ViewToolbar/ColumnDragOverlay";
import {
    ColumnItem,
    ColumnItemOverlay,
} from "@design-system/ViewToolbar/ColumnItem";
import { useColumnSettingsSensors } from "@design-system/ViewToolbar/useColumnSettingsSensors";
import { VIEW_PIN } from "../adapters/savedViewColumns";
import type { DataGridPin, DataGridTable } from "../columns/types";
import {
    type ColumnSettingsZone,
    moveColumn,
    pinColumn,
} from "./columnSettingsActions";

const FROM_MENU_PIN: Record<ColumnPin, DataGridPin> = {
    left: "start",
    right: "end",
};

/** "Amount column, position 2 of 5", from a zone's rows. */
const useZoneCopy = (zone: ColumnSettingsZone) =>
    useMemo(() => {
        const ids = zone.rows.map((row) => row.id);
        const nameOf = (id: UniqueIdentifier | undefined) =>
            zone.rows.find((row) => row.id === String(id))?.name ?? "Column";
        const positionOf = (id: UniqueIdentifier) =>
            `position ${ids.indexOf(String(id)) + 1} of ${ids.length}`;
        const announcements: Announcements = {
            onDragStart: ({ active }) =>
                `Picked up ${nameOf(active.id)} column.`,
            onDragOver: ({ active, over }) =>
                over
                    ? `${nameOf(active.id)} column is at ${positionOf(over.id)}.`
                    : undefined,
            onDragEnd: ({ active, over }) =>
                over
                    ? `Moved ${nameOf(active.id)} column to ${positionOf(over.id)}.`
                    : `${nameOf(active.id)} column stayed in place.`,
            onDragCancel: ({ active }) =>
                `Canceled moving ${nameOf(active.id)} column.`,
        };
        return { ids, announcements };
    }, [zone.rows]);

/**
 * One pin zone of the column settings list: rows drag (pointer, or Space
 * and the arrow keys on the handle) within the zone, and a drop goes
 * through `moveColumn`, the same seam header drags use.
 */
export const ColumnSettingsZoneList = <TData extends RowData>({
    table,
    zone,
    scrollContainerRef,
    onPinned,
}: Readonly<{
    table: DataGridTable<TData>;
    zone: ColumnSettingsZone;
    /** The popover's list: the only element a drag may auto-scroll. */
    scrollContainerRef: RefObject<HTMLElement | null>;
    /** After a row's pin changes, with the column's name. */
    onPinned: (name: string) => void;
}>) => {
    const [activeId, setActiveId] = useState<string | null>(null);
    const sensors = useColumnSettingsSensors();
    const { ids, announcements } = useZoneCopy(zone);
    const activeRow = zone.rows.find((row) => row.id === activeId);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            accessibility={{ announcements }}
            autoScroll={{
                canScroll: (element) => element === scrollContainerRef.current,
            }}
            onDragStart={({ active }) => setActiveId(String(active.id))}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={({ active, over }) => {
                setActiveId(null);
                if (over && over.id !== active.id) {
                    moveColumn(table, {
                        columnId: String(active.id),
                        toIndex: ids.indexOf(String(over.id)),
                    });
                }
            }}
        >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {zone.rows.map((row) => (
                    <ColumnItem
                        key={row.id}
                        id={row.id}
                        name={row.name}
                        isVisible={row.isVisible}
                        isPinned={row.pin && VIEW_PIN[row.pin]}
                        required={!row.canHide}
                        isDraggable={ids.length > 1}
                        isDragInProgress={activeId !== null}
                        onToggleVisibility={() =>
                            table.getColumn(row.id)?.toggleVisibility()
                        }
                        onPin={(pin) => {
                            pinColumn(table, {
                                columnId: row.id,
                                pin: pin && FROM_MENU_PIN[pin],
                            });
                            onPinned(row.name);
                        }}
                    />
                ))}
            </SortableContext>
            <ColumnDragOverlay>
                {activeRow && (
                    <ColumnItemOverlay
                        name={activeRow.name}
                        isPinned={activeRow.pin && VIEW_PIN[activeRow.pin]}
                        isLocked={!activeRow.canHide}
                        isVisible={activeRow.isVisible}
                        hasPinMenu
                    />
                )}
            </ColumnDragOverlay>
        </DndContext>
    );
};
